import {
    Clinic,
    Cohort,
    PatientAssociation,
    AccessLevel
} from '../interfaces/Clinic';
import { 
    UserOption,
    ClinicAccessWithUserDetails,
    CohortAccessWithUserDetails
} from '../interfaces/accessControl/AccessControl';
import * as CONSTANTS from '../utils/constants/constants';

// Request types for API calls
interface GrantAccessRequest {
    clinic_id: number;
    user_id: number;
    access_level: AccessLevel;
    granted_by_id: number;
}

interface GrantCohortAccessRequest {
    user_id: number;
    cohort_id: number;
    access_level: AccessLevel;
    granted_by_id: number;
}

interface PatientAssignmentRequest {
    cohort: number;
    patient_id: string;
    notes: string;
}

interface BulkPatientAssignmentRequest {
    cohort: number;
    patient_ids: string[];
    notes: string;
}

const BASE_URL = `${CONSTANTS.HOST}api/v1/cohort`;

class AccessControlService {
    private getHeaders(token: string) {
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
    }

    private async handleResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            const errorText = await response.text();
            console.error('API Error:', response.status, response.url, errorText);
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        return response.json();
    }

    private normalizeAccessId(access: any, candidateFields: string[]) {
        for (const field of candidateFields) {
            const value = access?.[field];
            if (value !== undefined && value !== null && value !== '') {
                const numericValue = Number(value);
                return Number.isNaN(numericValue) ? value : numericValue;
            }
        }
        return undefined;
    }

    // Clinic Management
    async getClinics(token: string): Promise<Clinic[]> {
        // Try to get clinics with cohorts included if the API supports it
        const response = await fetch(`${BASE_URL}/clinics/?include_cohorts=true`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });
        const data = await this.handleResponse<any>(response);
        
                        // Handle paginated response format
        let clinicsData: any[] = [];
        if (data && typeof data === 'object') {
            if (Array.isArray(data.results)) {
                clinicsData = data.results;
            } else if (Array.isArray(data.data)) {
                clinicsData = data.data;
            } else if (Array.isArray(data.clinics)) {
                clinicsData = data.clinics;
            } else if (Array.isArray(data)) {
                clinicsData = data;
            }
        }
        
        // Transform the data to match our interface (clinic_id -> id)
        return clinicsData.map((clinic: any) => ({
            ...clinic,
            id: clinic.clinic_id || clinic.id, // Handle both field names
        }));
    }

    async getClinic(clinicId: number, token: string): Promise<Clinic> {
        const response = await fetch(`${BASE_URL}/clinics/${clinicId}/`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });
        return this.handleResponse<Clinic>(response);
    }

    async createClinic(clinic: { name: string; description: string }, token: string): Promise<Clinic> {
        const response = await fetch(`${BASE_URL}/clinics/`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: JSON.stringify(clinic),
        });
        return this.handleResponse<Clinic>(response);
    }

    async updateClinic(clinicId: number, clinic: { name: string; description: string }, token: string): Promise<Clinic> {
        const response = await fetch(`${BASE_URL}/clinics/${clinicId}/`, {
            method: 'PUT',
            headers: this.getHeaders(token),
            body: JSON.stringify(clinic),
        });
        return this.handleResponse<Clinic>(response);
    }

    async deleteClinic(clinicId: number, token: string): Promise<void> {
        const response = await fetch(`${BASE_URL}/clinics/${clinicId}/`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
    }

    // Clinic Access Management
    async getClinicAccess(clinicId: number, token: string): Promise<ClinicAccessWithUserDetails[]> {
        const response = await fetch(`${BASE_URL}/clinics/${clinicId}/access/`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });
        const data = await this.handleResponse<any>(response);
        
        // Handle paginated response format
        let accesses: any[] = [];
        if (data && typeof data === 'object') {
            if (Array.isArray(data.results)) {
                accesses = data.results;
            } else if (Array.isArray(data.data)) {
                accesses = data.data;
            } else if (Array.isArray(data.accesses)) {
                accesses = data.accesses;
            } else if (Array.isArray(data.access)) {
                accesses = data.access;
            } else if (Array.isArray(data)) {
                accesses = data;
            }
        }
        return accesses.map((access: any) => {
            const normalizedId = this.normalizeAccessId(access, ['id', 'access_id', 'clinic_access_id']);
            if (normalizedId === undefined) {
                console.warn('Clinic access missing identifier. Unable to guarantee revoke/update operations.', access);
            }
            const normalizedClinicId = access?.clinic_id ?? access?.clinic ?? access?.clinic?.id ?? access?.clinic?.clinic_id;
            return {
                ...access,
                id: normalizedId ?? access?.id,
                clinic_id: normalizedClinicId,
                clinic_name: access?.clinic_name ?? access?.clinic?.name ?? access?.clinic_name,
            };
        });
    }

    async grantClinicAccess(clinicId: number, request: GrantAccessRequest, token: string): Promise<ClinicAccessWithUserDetails> {
        const response = await fetch(`${BASE_URL}/clinics/${clinicId}/grant-access/`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: JSON.stringify(request),
        });
        return this.handleResponse<ClinicAccessWithUserDetails>(response);
    }

    async updateClinicAccess(accessId: number, updates: Partial<ClinicAccessWithUserDetails>, token: string): Promise<ClinicAccessWithUserDetails> {
        const response = await fetch(`${BASE_URL}/clinic-access/${accessId}/`, {
            method: 'PUT',
            headers: this.getHeaders(token),
            body: JSON.stringify(updates),
        });
        return this.handleResponse<ClinicAccessWithUserDetails>(response);
    }

    async revokeClinicAccess(accessId: number, token: string): Promise<void> {
        await this.updateClinicAccess(accessId, { is_active: false }, token);
    }

    // Cohort Management
    async getCohorts(token: string): Promise<Cohort[]> {
        try {
            // First try to get clinics with cohorts included
            const clinics = await this.getClinics(token);
            
            // Check if clinics have cohorts already loaded
            const cohortsFromClinics = clinics
                .filter(clinic => clinic.cohorts && clinic.cohorts.length > 0)
                .flatMap(clinic => clinic.cohorts!);
                
            if (cohortsFromClinics.length > 0) {
                return cohortsFromClinics;
            }
            
            // If no cohorts in clinics response, fetch them individually
            const allCohorts: Cohort[] = [];
            for (const clinic of clinics) {
                try {
                    const clinicCohorts = await this.getCohortsForClinic(clinic.id, token);
                    allCohorts.push(...clinicCohorts);
                } catch (error) {
                    console.warn(`Failed to get cohorts for clinic ${clinic.id}:`, error);
                }
            }
            
            return allCohorts;
        } catch (error) {
            console.error('Failed to get cohorts:', error);
            return [];
        }
    }

    async getCohortsForClinic(clinicId: number, token: string): Promise<Cohort[]> {
        const response = await fetch(`${BASE_URL}/cohorts/?clinic_id=${clinicId}`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });
        const data = await this.handleResponse<any>(response);
        
        // Handle paginated response format
        let cohortsData: any[] = [];
        if (data && typeof data === 'object' && Array.isArray(data.results)) {
            cohortsData = data.results;
        } else if (Array.isArray(data)) {
            cohortsData = data;
        }
        
        // Transform the data to match our interface if needed (cohort_id -> id)
        return cohortsData.map((cohort: any) => ({
            ...cohort,
            id: cohort.cohort_id || cohort.id, // Handle both field names
        }));
    }

    async getCohort(cohortId: number, token: string): Promise<Cohort> {
        const response = await fetch(`${BASE_URL}/cohorts/${cohortId}/`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });
        return this.handleResponse<Cohort>(response);
    }

    async createCohort(cohort: { name: string; description: string; clinic_id: number }, token: string): Promise<Cohort> {
        const response = await fetch(`${BASE_URL}/cohorts/`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: JSON.stringify(cohort),
        });
        return this.handleResponse<Cohort>(response);
    }

    async updateCohort(cohortId: number, cohort: { name: string; description: string; clinic_id: number }, token: string): Promise<Cohort> {
        const response = await fetch(`${BASE_URL}/cohorts/${cohortId}/`, {
            method: 'PUT',
            headers: this.getHeaders(token),
            body: JSON.stringify(cohort),
        });
        return this.handleResponse<Cohort>(response);
    }

    async deleteCohort(cohortId: number, token: string): Promise<void> {
        const response = await fetch(`${BASE_URL}/cohorts/${cohortId}/`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
    }

    // Cohort Access Management
    async getCohortAccess(token: string): Promise<CohortAccessWithUserDetails[]> {
        const response = await fetch(`${BASE_URL}/cohort-access/`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });
        const data = await this.handleResponse<any>(response);
        
        // Handle paginated response format
        let accesses: any[] = [];
        if (data && typeof data === 'object') {
            if (Array.isArray(data.results)) {
                accesses = data.results;
            } else if (Array.isArray(data.data)) {
                accesses = data.data;
            } else if (Array.isArray(data.accesses)) {
                accesses = data.accesses;
            } else if (Array.isArray(data.access)) {
                accesses = data.access;
            } else if (Array.isArray(data)) {
                accesses = data;
            }
        }
        return accesses.map((access: any) => {
            const normalizedId = this.normalizeAccessId(access, ['id', 'access_id', 'cohort_access_id']);
            if (normalizedId === undefined) {
                console.warn('Cohort access missing identifier. Unable to guarantee revoke/update operations.', access);
            }
            const normalizedCohortId = access?.cohort_id ?? access?.cohort ?? access?.cohort?.id ?? access?.cohort?.cohort_id;
            return {
                ...access,
                id: normalizedId ?? access?.id,
                cohort_id: normalizedCohortId,
                cohort_name: access?.cohort_name ?? access?.cohort?.name ?? access?.cohort_name,
            };
        });
    }

    async grantCohortAccess(request: GrantCohortAccessRequest, token: string): Promise<CohortAccessWithUserDetails> {
        const response = await fetch(`${BASE_URL}/cohort-access/`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: JSON.stringify(request),
        });
        return this.handleResponse<CohortAccessWithUserDetails>(response);
    }

    async updateCohortAccess(accessId: number, updates: Partial<CohortAccessWithUserDetails>, token: string): Promise<CohortAccessWithUserDetails> {
        const response = await fetch(`${BASE_URL}/cohort-access/${accessId}/`, {
            method: 'PUT',
            headers: this.getHeaders(token),
            body: JSON.stringify(updates),
        });
        return this.handleResponse<CohortAccessWithUserDetails>(response);
    }

    async revokeCohortAccess(accessId: number, token: string): Promise<void> {
        const response = await fetch(`${BASE_URL}/cohort-access/${accessId}/`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
    }

    // Patient Assignment Management
    async getPatientAssociations(token: string): Promise<PatientAssociation[]> {
        const response = await fetch(`${BASE_URL}/patient-associations/`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });
        return this.handleResponse<PatientAssociation[]>(response);
    }

    async assignPatientToCohort(request: PatientAssignmentRequest, token: string): Promise<PatientAssociation> {
        const response = await fetch(`${BASE_URL}/patient-associations/`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: JSON.stringify(request),
        });
        return this.handleResponse<PatientAssociation>(response);
    }

    async bulkAssignPatientsToCohort(request: BulkPatientAssignmentRequest, token: string): Promise<PatientAssociation[]> {
        const response = await fetch(`${BASE_URL}/patient-associations/bulk/`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: JSON.stringify(request),
        });
        return this.handleResponse<PatientAssociation[]>(response);
    }

    async removePatientFromCohort(associationId: number, token: string): Promise<void> {
        const response = await fetch(`${BASE_URL}/patient-associations/${associationId}/`, {
            method: 'DELETE',
            headers: this.getHeaders(token),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
    }

    // Helper method to get eligible users for assignment (nurses)
    async getEligibleUsers(token: string): Promise<UserOption[]> {
        // This would call the existing user management API
        // Filter for orgadmin, staffuser, physician roles
        const response = await fetch(`${CONSTANTS.HOST}api/v1/sysadmin/users/`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        
        // Handle paginated response if needed
        let users: any[] = [];
        if (data && typeof data === 'object' && Array.isArray(data.results)) {
            users = data.results;
        } else if (data && typeof data === 'object' && Array.isArray(data.users)) {
            users = data.users;
        } else if (Array.isArray(data)) {
            users = data;
        }
        
        // Filter for eligible roles: orgadmin, staffuser, physician, nurse
        const filteredUsers = users.filter((user: any) => {
            const roleType = typeof user.role === 'string' ? user.role : user.role?.roletype;
            return roleType && ['orgadmin', 'staffuser', 'physician', 'nurse'].includes(roleType);
        });
        
        return filteredUsers;
    }
}

const accessControlService = new AccessControlService();

export default accessControlService;
